import type { ReactElement, RefObject } from "react";
import NextLink from "next/link";
import { useRouter } from "next/router";
import {
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Icon,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { As, StackProps } from "@chakra-ui/react";
import { RiLoginBoxLine, RiLogoutBoxLine, RiUser3Line } from "react-icons/ri";
import { useAppDrawer } from "stores/use-app-drawer";
import useUser from "hooks/use-user";
import type { SidebarLink } from "./app-links";

interface DrawerItemProps extends StackProps {
  icon: As;
  label: string;
  isExternal?: boolean;
  "data-cy"?: string;
  isDisabled?: boolean;
  href?: string;
  onClick?: () => void;
}

const DrawerItem = ({
  icon,
  label,
  isExternal,
  isDisabled,
  href,
  onClick,
  ...props
}: DrawerItemProps) => {
  const getAs = () => {
    if (isDisabled) {
      return undefined;
    }

    if (href) {
      return NextLink;
    }

    if (onClick) {
      return "button";
    }

    return undefined;
  };

  return (
    <Stack
      as={getAs()}
      href={href}
      py={2}
      direction="row"
      align="center"
      cursor={isDisabled ? "not-allowed" : "pointer"}
      aria-label={label}
      target={isExternal ? "_blank" : "_self"}
      rel={isExternal ? "noopener noreferrer" : undefined}
      opacity={isDisabled ? 0.5 : 1}
      role={onClick ? "button" : undefined}
      onClick={onClick}
      {...props}
    >
      <Center
        outline="none"
        rounded="md"
        transition=".2s all"
        _focus={{
          boxShadow: "outline",
          bg: "slate.200",
        }}
      >
        <Icon color="blue.500" h={6} w={6} as={icon} />
      </Center>
      <Text>{label}</Text>
    </Stack>
  );
};

interface AppDrawerProps {
  drawerButtonRef: RefObject<HTMLButtonElement>;
  sidebarLinks: SidebarLink[];
}

const AppDrawer = ({
  drawerButtonRef,
  sidebarLinks,
}: AppDrawerProps): ReactElement => {
  const user = useUser();
  const { asPath } = useRouter();

  const { isOpen, onClose } = useAppDrawer((store) => ({
    isOpen: store.isOpen,
    onClose: store.onClose,
  }));

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      finalFocusRef={drawerButtonRef}
      blockScrollOnMount={false}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Menu</DrawerHeader>

        <DrawerBody as="nav">
          <Stack my={4} justify="flex-start" spacing={4}>
            {user.isLoggedIn ? (
              <>
                {sidebarLinks.map(
                  ({
                    href,
                    key,
                    icon,
                    label,
                    isExternal,
                    isDisabled,
                    isHidden,
                  }) =>
                    isHidden ? null : (
                      <DrawerItem
                        key={key}
                        label={label}
                        icon={icon}
                        isExternal={isExternal}
                        data-cy={`nav-drawer-link-${key}`}
                        isDisabled={isDisabled}
                        href={href}
                      />
                    )
                )}

                <Divider />

                <DrawerItem
                  label="Account"
                  href="/account"
                  icon={RiUser3Line}
                />

                <DrawerItem
                  as="button"
                  role="button"
                  onClick={() => user.signOut()}
                  label="Sign Out"
                  icon={RiLogoutBoxLine}
                />
              </>
            ) : (
              <DrawerItem
                label="Sign In"
                href={`/signin?${new URLSearchParams({ redirect: asPath })}`}
                icon={RiLoginBoxLine}
              />
            )}
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default AppDrawer;
